import { useCallback, useEffect, useState } from 'react';
import DataTable from '../../../components/tables/DataTable';
import Switch from '../../../components/Switch';
import Modal from '../../../components/modal';
import { getStoreEmailSettings, saveStoreEmailSettings, previewStoreEmailTemplate } from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';

// Static for now -- these are the customer-facing transactional emails the
// store sends. `key` is the template file name the API resolves (custom
// email_templates/<store_code>/ or default/). Whether each one is sent to
// customers is controlled on the Notifications page, not here.
const EMAIL_TEMPLATES = [
	{ key: 'order-confirmation', name: 'Order Confirmation' },
	{ key: 'shipping-notification', name: 'Order Shipped' },
	{ key: 'order-cancelled', name: 'Order Cancelled' },
	{ key: 'order-on-hold', name: 'Order On Hold' },
	{ key: 'order-delayed', name: 'Order Delayed' },
];

export default function EmailTemplatesTab({ storeId }) {
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const [settings, setSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);

	const load = useCallback(() => getStoreEmailSettings(storeId), [storeId]);

	useEffect(() => {
		setLoading(true);
		load().then((result) => {
			if (result?.success) setSettings(result.data || {});
			setLoading(false);
		});
	}, [load]);

	const [preview, setPreview] = useState(null); // { name, loading, error, data }

	const openPreview = async (template) => {
		setPreview({ name: template.name, loading: true });
		const result = await previewStoreEmailTemplate(storeId, template.key);
		setPreview((current) =>
			current?.name === template.name
				? {
						name: template.name,
						loading: false,
						data: result?.success ? result.data : null,
						error: result?.success ? null : result?.error || 'Failed to load preview.',
					}
				: current,
		);
	};

	const useCustomTemplates = settings?.useCustomEmailTemplates === true;
	// Derived by the API: whether email_templates/<store_code>/ exists. Turning
	// custom templates on is blocked without it; turning an already-on switch
	// off is still allowed.
	const customTemplatesAvailable = settings?.customTemplatesAvailable === true;
	const customTemplatesFolder = settings?.customTemplatesFolder || 'email_templates/<store code>/';
	const switchBlocked = !customTemplatesAvailable && !useCustomTemplates;

	const toggleTemplateType = async (nextChecked) => {
		const previous = settings;
		setSettings((current) => ({ ...current, useCustomEmailTemplates: nextChecked }));
		setSaving(true);
		setError(null);

		const result = await saveStoreEmailSettings(storeId, { useCustomEmailTemplates: nextChecked });
		setSaving(false);

		if (!result?.success) {
			setSettings(previous);
			setError(result?.error || 'Failed to save template type.');
		}
	};

	const rows = EMAIL_TEMPLATES.map((template) => ({
		key: template.key,
		name: template.name,
		type: useCustomTemplates ? 'Custom' : 'Default',
	}));

	const columns = [
		{ key: 'name', header: 'Automation' },
		{ key: 'type', header: 'Type' },
		{
			key: 'preview',
			header: '',
			sortable: false,
			render: (_value, row) => (
				<div className='text-right'>
					<button type='button' className='btn-secondary' onClick={() => openPreview(row)}>
						Preview
					</button>
				</div>
			),
		},
	];

	return (
		<div>
			<p className='text-sm text-gray-500 mb-4'>
				These are the transactional emails sent to customers for this store. Use Preview to see
				each one with sample data. Editing template content is coming in a future phase — use{' '}
				<span className='font-medium text-gray-700'>Store &rarr; Notifications</span> to turn a
				specific email on or off.
			</p>

			<div className='pt-3 pb-8 border-t border-gray-100'>
				<Switch
					checked={useCustomTemplates}
					disabled={saving || loading || !canEdit || switchBlocked}
					onChange={toggleTemplateType}
					label='Use custom email templates for this store'
				/>
				{error && <p className='text-sm text-red-600 mt-2'>{error}</p>}
				{!loading && !customTemplatesAvailable && (
					<div role='alert' className='mt-3 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
						{useCustomTemplates ? (
							<>
								Custom templates are turned on, but this store has no custom template folder (
								<code className='font-mono text-xs'>{customTemplatesFolder}</code>), so customers are
								receiving the default templates. Turn this off, or add the folder to the API.
							</>
						) : (
							<>
								Custom templates can't be enabled because this store has no custom template folder.
								Add one at <code className='font-mono text-xs'>{customTemplatesFolder}</code> in
								pi_ecommerce_api and deploy it, then this switch becomes available.
							</>
						)}
					</div>
				)}
				<p className='text-sm text-gray-400 mt-2'>
					When on, emails use this store's templates in{' '}
					<code className='font-mono text-xs'>{customTemplatesFolder}</code>. Any email without a
					custom file there falls back to the default template.
				</p>
			</div>

			<DataTable columns={columns} data={rows} loading={loading} emptyHeading='No email templates found' />

			<Modal
				open={Boolean(preview)}
				setOpen={(open) => !open && setPreview(null)}
				title={preview ? `${preview.name} preview` : ''}
				size='lg'
			>
				{preview?.loading && <p className='text-sm text-gray-400'>Loading preview…</p>}
				{preview?.error && <p className='text-sm text-red-600'>{preview.error}</p>}
				{preview?.data && (
					<div className='space-y-4'>
						<div className='border border-gray-200 bg-gray-50 px-4 py-3 text-sm'>
							<p>
								<span className='text-gray-500'>Subject: </span>
								<span className='font-medium text-gray-900'>{preview.data.subject}</span>
							</p>
							<p className='text-xs text-gray-400 mt-1'>
								{preview.data.source === 'custom' ? 'Custom template' : 'Default template'} · rendered
								with sample order and customer data
							</p>
						</div>
						<iframe
							title={`${preview.name} email preview`}
							srcDoc={preview.data.html}
							sandbox=''
							className='w-full h-[65vh] border border-gray-200 bg-white'
						/>
					</div>
				)}
				<div className='flex justify-end pt-4'>
					<button type='button' className='btn-secondary' onClick={() => setPreview(null)}>
						Close
					</button>
				</div>
			</Modal>
		</div>
	);
}
